import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSeguroFicha } from "./SeguroFicha";
import usePermissions from "../../hook/usePermissions";
import { ListadoPlanes, CambiarEstadoPlan } from "../../Service/Service.Plan";
import DataTable from "../../Components/UI/DataTable/DataTable";
import { Celda } from "../../Components/UI/DataTable/piezas";
import MenuAcciones from "../../Components/UI/MenuAcciones";
import ModalKit from "../../Components/UI/Modal/Modal";
import Boton from "../../Components/UI/Boton";
import { ChipEstado } from "../../Components/UI/Chip";
import { CampoTexto, CampoSelect } from "../../Components/UI/Campo";
import { useToast } from "../../Components/UI/Aviso";
import {
    IconoMas,
    IconoEditar,
    IconoCopiar,
    IconoCheck,
    IconoProhibido,
    IconoBuscar,
} from "../../Components/UI/Iconos";
import "../../styles/Seguros.css";

/* ============================================================================
   SEGUROS-PLANES FASE 3 · planes de ESTE seguro.

   Hoy la sección Planes muestra los de todas las aseguradoras mezclados y hay
   que elegir el seguro otra vez en un selector. Aquí el seguro viene de la
   ruta: la lista llega ya filtrada y no se vuelve a preguntar.

   PROCEDIMIENTOS Y COPAGO PROMEDIO SALEN GRATIS: `GET /planes?seguro_id` ya
   devuelve cada plan con todos sus `detalles` hidratados (28 KB para las 15
   aseguradoras del seguro más cargado). No hace falta ninguna petición extra
   ni ningún endpoint nuevo, así que se muestran. Si algún día ese endpoint
   dejara de traer los detalles, lo correcto sería quitar las dos columnas —
   no inventar una consulta por fila.

   VIGENCIA NO SE MUESTRA: no existe en el modelo. `plan` tiene `id_plan`,
   `nombre`, `estado` y `seguro_id`, y nada más. Inventar la columna exigiría
   DDL y una regla de negocio nueva, que es justo lo que este encargo prohíbe.

   ELIMINAR TAMPOCO: CONFIG-PLANES-SEGURO-31 retiró el borrado a propósito —
   un plan borrado se lleva por delante facturas, prestaciones, resultados y
   pacientes históricos. Se inactiva. No lo reintroduzco.
   ========================================================================== */

const ESTADO_OPCIONES = [
    { value: "Activo", label: "Activos" },
    { value: "Inactivo", label: "Inactivos" },
    { value: "Todos", label: "Todos" },
];

/**
 * Copago promedio de un plan: media simple de los copagos de sus líneas.
 *
 * Es una media de PORCENTAJES sin ponderar por importe, y así se etiqueta
 * ("promedio"). No es un dato de negocio ni se guarda en ninguna parte: es un
 * resumen de pantalla para distinguir de un vistazo un plan al 20 % de uno al
 * 80 %. Ponderarlo por precio sería inventar un cálculo que el sistema no hace
 * en ningún sitio.
 */
function copagoPromedio(detalles) {
    if (!Array.isArray(detalles) || detalles.length === 0) return null;
    const suma = detalles.reduce((acc, d) => acc + Number(d.copago || 0), 0);
    return suma / detalles.length;
}

export default function SeguroPlanes() {
    const navigate = useNavigate();
    const { seguro, idseguro, recargarContadores } = useSeguroFicha();
    const { hasPermission } = usePermissions();
    const { mostrar } = useToast();

    const [planes, setPlanes] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(null);

    const [busquedaInput, setBusquedaInput] = useState("");
    const [busqueda, setBusqueda] = useState("");
    const [estado, setEstado] = useState("Activo");

    const [porCambiar, setPorCambiar] = useState(null); // { plan, nuevoEstado }
    const [cambiando, setCambiando] = useState(false);

    const base = `/configuracion/seguros/${idseguro}`;

    const cargar = useCallback(async () => {
        setCargando(true);
        const params = { estado, seguro_id: idseguro };
        if (busqueda) params.search = busqueda;

        const respuesta = await ListadoPlanes(params);
        if (respuesta?.data) {
            setPlanes(
                respuesta.data.map((p) => ({
                    ...p,
                    idplan: p.id_plan,
                    procedimientos: Array.isArray(p.detalles) ? p.detalles.length : null,
                    copago: copagoPromedio(p.detalles),
                }))
            );
            setError(null);
        } else {
            // Un fallo y un filtro sin resultados no pueden tener la misma
            // pinta: uno se reintenta, el otro se corrige.
            setPlanes([]);
            setError("No se pudo obtener el listado de planes.");
        }
        setCargando(false);
    }, [estado, busqueda, idseguro]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    // La búsqueda va al servidor: sin esta pausa, escribir "LABORATORIO" serían
    // once peticiones.
    useEffect(() => {
        const t = setTimeout(() => setBusqueda(busquedaInput.trim()), 300);
        return () => clearTimeout(t);
    }, [busquedaInput]);

    const abrirConstructor = useCallback(
        (plan) => navigate(`${base}/planes/${plan.idplan}`),
        [navigate, base]
    );

    const cambiarEstado = async () => {
        if (!porCambiar) return;
        setCambiando(true);
        try {
            const r = await CambiarEstadoPlan(porCambiar.plan.idplan, porCambiar.nuevoEstado);
            if (!r.success) throw new Error(r.message);
            mostrar({ tipo: "exito", texto: r.message || "Estado actualizado" });
            setPorCambiar(null);
            await cargar();
            // El contador de la pestaña cuenta solo los Activos: inactivar un
            // plan tiene que reflejarse ahí, no quedarse desfasado hasta la
            // siguiente recarga completa.
            recargarContadores();
        } catch (e) {
            mostrar({ tipo: "error", texto: e.message || "No se pudo cambiar el estado del plan" });
        } finally {
            setCambiando(false);
        }
    };

    const puedeEditar = hasPermission("planes.edit");
    const puedeCrear = hasPermission("planes.create");

    const columnas = useMemo(
        () => [
            {
                key: "nombre",
                titulo: "Plan",
                render: (p) => <Celda valor={p.nombre} />,
            },
            {
                key: "procedimientos",
                titulo: "Procedimientos",
                ancho: 130,
                alinear: "derecha",
                render: (p) =>
                    Number.isFinite(p.procedimientos) ? (
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>{p.procedimientos}</span>
                    ) : (
                        <span className="sg-dato__valor--vacio">—</span>
                    ),
            },
            {
                key: "copago",
                titulo: "Copago promedio",
                ancho: 145,
                alinear: "derecha",
                render: (p) =>
                    p.copago === null ? (
                        <span className="sg-dato__valor--vacio">—</span>
                    ) : (
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                            {p.copago.toFixed(1)} %
                        </span>
                    ),
            },
            {
                key: "estado",
                titulo: "Estado",
                ancho: 115,
                render: (p) => (
                    <ChipEstado tono={p.estado === "Inactivo" ? "anulado" : "hecho"}>
                        {p.estado === "Inactivo" ? "Inactivo" : "Activo"}
                    </ChipEstado>
                ),
            },
            {
                key: "acciones",
                titulo: "",
                ancho: 60,
                alinear: "centro",
                render: (p) => {
                    const acciones = [];
                    if (puedeEditar) {
                        acciones.push({
                            label: "Editar",
                            icono: IconoEditar,
                            onClick: () => abrirConstructor(p),
                        });
                    }
                    if (puedeCrear) {
                        // Duplicar no necesita ningún endpoint: el constructor
                        // lee el plan origen del mismo listado que ya se pidió.
                        acciones.push({
                            label: "Duplicar",
                            icono: IconoCopiar,
                            onClick: () => navigate(`${base}/planes/nuevo?duplicar=${p.idplan}`),
                        });
                    }
                    if (puedeEditar) {
                        const inactivo = p.estado === "Inactivo";
                        acciones.push(
                            inactivo
                                ? {
                                      label: "Activar",
                                      icono: IconoCheck,
                                      onClick: () => setPorCambiar({ plan: p, nuevoEstado: "Activo" }),
                                  }
                                : {
                                      label: "Inactivar",
                                      icono: IconoProhibido,
                                      peligro: true,
                                      onClick: () => setPorCambiar({ plan: p, nuevoEstado: "Inactivo" }),
                                  }
                        );
                    }
                    if (acciones.length === 0) return null;
                    return <MenuAcciones titulo={`Acciones de ${p.nombre}`} acciones={acciones} />;
                },
            },
        ],
        [puedeEditar, puedeCrear, abrirConstructor, navigate, base]
    );

    const hayFiltros = Boolean(busqueda) || estado !== "Activo";

    return (
        <section>
            <div
                style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    marginBottom: 14,
                }}
            >
                <div style={{ flex: "1 1 260px", maxWidth: 360 }}>
                    <CampoTexto
                        etiqueta="Buscar plan"
                        icono={IconoBuscar}
                        placeholder="Nombre del plan"
                        value={busquedaInput}
                        onChange={(e) => setBusquedaInput(e.target.value)}
                        data-testid="plan-buscar"
                    />
                </div>
                <div style={{ width: 160 }}>
                    <CampoSelect
                        etiqueta="Estado"
                        opciones={ESTADO_OPCIONES}
                        value={estado}
                        onChange={(e) => setEstado(e.target.value)}
                        data-testid="plan-estado"
                    />
                </div>
                {puedeCrear && (
                    <div style={{ marginLeft: "auto" }}>
                        <Boton
                            variante="primario"
                            icono={IconoMas}
                            onClick={() => navigate(`${base}/planes/nuevo`)}
                            data-testid="plan-nuevo"
                        >
                            Nuevo plan
                        </Boton>
                    </div>
                )}
            </div>

            <DataTable
                columnas={columnas}
                filas={planes}
                filaKey="idplan"
                cargando={cargando}
                error={error}
                onReintentar={cargar}
                onFilaClick={puedeEditar ? abrirConstructor : undefined}
                hayFiltrosAplicados={hayFiltros}
                onLimpiarFiltros={() => {
                    setBusquedaInput("");
                    setEstado("Activo");
                }}
                textoVacioSinFiltros={`${seguro.nombre} todavía no tiene planes.`}
                accionesVacio={
                    puedeCrear
                        ? [
                              {
                                  label: "Crear el primer plan",
                                  primaria: true,
                                  onClick: () => navigate(`${base}/planes/nuevo`),
                              },
                          ]
                        : []
                }
                etiquetaTabla={`Planes de ${seguro.nombre}`}
            />

            {/* Trampa 7: nunca un modal sobre otro. Este es el único de la
                pantalla, y sólo existe mientras hay algo que confirmar. */}
            <ModalKit
                abierto={Boolean(porCambiar)}
                onCerrar={() => setPorCambiar(null)}
                titulo={porCambiar?.nuevoEstado === "Activo" ? "Activar plan" : "Inactivar plan"}
                destructivo={porCambiar?.nuevoEstado !== "Activo"}
                secundario={{ label: "Cancelar" }}
                primario={{
                    label: porCambiar?.nuevoEstado === "Activo" ? "Activar" : "Inactivar",
                    cargando: cambiando,
                    onClick: cambiarEstado,
                }}
            >
                {porCambiar?.nuevoEstado === "Activo" ? (
                    <p>
                        El plan <strong>{porCambiar?.plan?.nombre}</strong> volverá a estar
                        disponible para facturar.
                    </p>
                ) : (
                    <p>
                        El plan <strong>{porCambiar?.plan?.nombre}</strong> dejará de estar
                        disponible para facturar. No se borra nada: las facturas, prestaciones y
                        resultados que ya lo usan se conservan, y puedes volver a activarlo cuando
                        quieras.
                    </p>
                )}
            </ModalKit>
        </section>
    );
}
